export type ProfileModalResult = {
  success?: boolean;
  redirect?: boolean;
  redirectTo?: string;
};

export type ProfileModalHandle = {
  element: { modal: () => void };
  close: Promise<ProfileModalResult | undefined>;
};

type ProfileShellHost = {
  dialogData: Record<string, unknown>;
};

type CloseModal = (result: ProfileModalResult, delay?: number) => void;

type ModalElement = {
  modal: (action: 'hide') => void;
};

export type OpenProfileModalOptions = {
  template: string;
  controller: unknown;
  controllerAs: 'vm';
};

export type OpenProfileModalPorts = {
  showModal: (options: OpenProfileModalOptions) => Promise<ProfileModalHandle>;
  onClose?: (result: ProfileModalResult | undefined) => void;
  logError?: (error: unknown) => void;
};

export const PROFILE_MODAL_TEMPLATE = [
  '<div class="modal modal-profile fade" id="profile-modal" tabindex="-1" role="dialog" aria-labelledby="profile-modal-label">',
  '<object type="image/svg+xml" data="assets/icons/profile-icons.svg" style="display: none"></object>',
  '<div class="modal-overlay" aria-label="Close"></div>',
  '<react-directive class="profile-dialog" component="\'ProfileDialog\'" data="vm.dialogData" refresh-on-change="true"></react-directive>',
  '</div>',
].join('');

/**
 * Unregistered shell. Angular modal service injects the host and the close handle.
 * `#profile-modal` stays so Bootstrap and the existing selectors keep working.
 */
export function profileShellController(
  this: ProfileShellHost,
  ProfileHost: unknown,
  close: CloseModal,
  $element: ModalElement
) {
  this.dialogData = {
    orchestrated: true,
    profileDialogEnabled: true,
    profilePageEnabled: true,
    embedInHostModal: true,
    host: ProfileHost,
    onDismiss: (result?: ProfileModalResult) => {
      $element.modal('hide');
      close(result || { success: false, redirect: false }, 200);
    },
  };
}

profileShellController.$inject = ['ProfileHost', 'close', '$element'];

const PROFILE_MODAL: OpenProfileModalOptions = {
  template: PROFILE_MODAL_TEMPLATE,
  controller: profileShellController,
  controllerAs: 'vm',
};

function followProfileClose(modal: ProfileModalHandle, ports: OpenProfileModalPorts): void {
  void modal.close
    .then((result) => {
      ports.onClose?.(result);
    })
    .catch((error: unknown) => {
      ports.logError?.(error);
    });
}

/**
 * Shared opener for the header and the e-agreements guard.
 * The unregistered shell keeps `#profile-modal` for Bootstrap and existing selectors.
 */
export async function openProfileModal(
  ports: OpenProfileModalPorts
): Promise<ProfileModalHandle | undefined> {
  try {
    const modal = await ports.showModal(PROFILE_MODAL);
    modal.element.modal();
    followProfileClose(modal, ports);
    return modal;
  } catch (error: unknown) {
    ports.logError?.(error);
    return undefined;
  }
}
